import { useCallback } from 'react';

import { useFunnelState } from 'scenes/common/contexts/quoteFlowContext';

import {
  MotorFormState,
  MotorStepType,
  MotorFunnelState,
  MotorFormAnswer,
  MotorApiResponses,
  MotorQuoteSubSection,
  MotorPolicySubSection,
  MotorCoverSubSection,
  MotorSubSection,
  MotorSection,
  MotorDriverSubSection,
  ReferenceData,
} from 'scenes/motor/types';

import analytics from 'scenes/motor/analytics';
import {
  nextIfLocationIs,
  nextPage,
  previousPage,
  restartQuote,
} from 'scenes/common/actions';
import {
  removeDriver,
  editDriver,
  editReviewDriver,
  apiResponse,
  editQuoteDetails,
  editPolicyDetails,
  editCoverDetails,
  updateDriver,
} from 'scenes/motor/actions';

const toStepType = (
  motorSubSection: MotorSubSection | undefined
): MotorStepType => {
  switch (motorSubSection) {
    case MotorQuoteSubSection.RecentInsurer:
      return MotorStepType.RecentInsurer;
    case MotorQuoteSubSection.AdditionalDrivers:
      return MotorStepType.AddDriver;
    case MotorDriverSubSection.FirstAndLastName:
      return MotorStepType.Name;
    case MotorDriverSubSection.GenderAndDateOfBirth:
      return MotorStepType.GenderAndDateOfBirth;
    case MotorDriverSubSection.YearsDriving:
      return MotorStepType.YearsDriving;
    case MotorDriverSubSection.ClaimsMade:
      return MotorStepType.ClaimsMade;
    case MotorDriverSubSection.PayableClaims:
      return MotorStepType.PayableClaims;
    case MotorQuoteSubSection.OvernightParkingAddress:
      return MotorStepType.OvernightParking;
    case MotorQuoteSubSection.VehicleDetails:
      return MotorStepType.VehicleDetails;
    case MotorQuoteSubSection.VehicleManualSelect:
      return MotorStepType.VehicleManualSelect;
    case MotorQuoteSubSection.IsUsedForBusiness:
      return MotorStepType.UsedForBusiness;
    case MotorQuoteSubSection.BusinessType:
      return MotorStepType.BusinessType;
    case MotorQuoteSubSection.VehicleFinance:
      return MotorStepType.VehicleFinancier;
    case MotorQuoteSubSection.FinanceType:
      return MotorStepType.Finance;
    case MotorQuoteSubSection.CoverForDriversUnder25:
      return MotorStepType.CoverForDriversUnder25;
    case MotorQuoteSubSection.FlybuysNumber:
      return MotorStepType.FlybuysNumber;
    case MotorPolicySubSection.InsuredSincePossessed:
      return MotorStepType.InsuredSincePossessed;
    case MotorPolicySubSection.PreExistingDamage:
      return MotorStepType.PreExistingDamage;
    case MotorPolicySubSection.VehicleModifications:
      return MotorStepType.Modifications;
    case MotorPolicySubSection.SpecialFeatures:
      return MotorStepType.SpecialFeatures;
    case MotorPolicySubSection.SpecialDetails:
      return MotorStepType.SpecialDetails;
    case MotorQuoteSubSection.PolicyStartDate:
      return MotorStepType.PolicyStartDate;
    case MotorPolicySubSection.PolicyIssuedTo:
      return MotorStepType.PersonalOrBusinessPolicy;
    case MotorPolicySubSection.PersonalPolicyContact:
      return MotorStepType.PersonalPolicyContact;
    case MotorPolicySubSection.BusinessPolicyContact:
      return MotorStepType.BusinessContact;
    case MotorPolicySubSection.ContactPhoneNumber:
      return MotorStepType.ContactPhoneNumber;
    case MotorPolicySubSection.ContactEmailAddress:
      return MotorStepType.ContactEmailAddress;
    case MotorPolicySubSection.PostalAddress:
      return MotorStepType.PostalAddress;
    case MotorPolicySubSection.BusinessNameAndAbn:
      return MotorStepType.BusinessNameAndAbn;
    case MotorPolicySubSection.BusinessRegisteredForGst:
      return MotorStepType.BusinessRegisteredForGst;
    case MotorPolicySubSection.BusinessContact:
      return MotorStepType.BusinessContact;
    case MotorPolicySubSection.AddPolicyHolder:
      return MotorStepType.AddPolicyHolder;

    case MotorCoverSubSection.QuotePrice:
      return MotorStepType.QuotePrice;
    case MotorCoverSubSection.MotorExcess:
      return MotorStepType.Excess;
    case MotorCoverSubSection.MarketAgreedValue:
      return MotorStepType.MarketAgreedValue;
    case MotorCoverSubSection.OptionalExtras:
      return MotorStepType.OptionalExtras;
    default:
      return MotorStepType.Name;
  }
};

export const useFlowActions = (
  handleAnswer: (f: MotorFormAnswer, autoMergeFormToState?: boolean) => void,
  referenceData: ReferenceData
) => {
  const funnelState = useFunnelState<MotorFunnelState>();

  const onNext = useCallback(
    (values: MotorFormState) => {
      handleAnswer(nextPage(values));
    },
    [handleAnswer]
  );

  /* Mimicks onNext behaviour but doesn't merge form data to the state slice
   * corresponding with the current funnel flow automatically (i.e overrides
   * the application's default behaviour  ).
   */
  const onNextNoAutoFormMerge = useCallback(
    (values: MotorFormState) => {
      handleAnswer(nextPage(values), false);
    },
    [handleAnswer]
  );

  const onNextIfLocationIs = useCallback(
    (step: MotorStepType, values: MotorFormState = {}) => {
      handleAnswer(nextIfLocationIs(step, values));
    },
    [handleAnswer]
  );

  const onRestartQuote = useCallback(() => {
    handleAnswer(restartQuote());
  }, [handleAnswer]);

  const onApiResponse = useCallback(
    (response: Partial<MotorApiResponses>) => {
      handleAnswer(apiResponse(response));
    },
    [handleAnswer]
  );

  const onPrevious = useCallback(() => {
    handleAnswer(previousPage());
  }, [handleAnswer]);

  const onRemoveDriver = useCallback(
    (index: number) => {
      handleAnswer(removeDriver(index));
    },
    [handleAnswer]
  );

  const onEditDriver = useCallback(
    (params: {
      index?: number;
      motorSection?: MotorSection;
      motorSubSection?: MotorDriverSubSection;
      nameEdit?: boolean;
    }) => {
      handleAnswer(
        editDriver(
          toStepType(params?.motorSubSection),
          params?.index,
          params?.nameEdit
        )
      );
    },
    [handleAnswer]
  );

  const onEditReviewDriver = useCallback(
    (index?: number) => {
      handleAnswer(editReviewDriver(index));
    },
    [handleAnswer]
  );

  const onEditQuoteDetails = useCallback(
    (motorSection: MotorSection, motorSubSection: MotorQuoteSubSection) => {
      analytics().edit(
        funnelState,
        referenceData,
        motorSection,
        motorSubSection
      );
      handleAnswer(editQuoteDetails(toStepType(motorSubSection)));
    },
    [funnelState, handleAnswer, referenceData]
  );

  const onEditPolicyDetails = useCallback(
    (motorSection: MotorSection, motorSubSection: MotorPolicySubSection) => {
      analytics().edit(
        funnelState,
        referenceData,
        motorSection,
        motorSubSection
      );
      handleAnswer(editPolicyDetails(toStepType(motorSubSection)));
    },
    [funnelState, handleAnswer, referenceData]
  );

  const onEditCoverDetails = useCallback(
    (motorSection: MotorSection, motorSubSection: MotorCoverSubSection) => {
      analytics().edit(
        funnelState,
        referenceData,
        motorSection,
        motorSubSection
      );
      handleAnswer(editCoverDetails(toStepType(motorSubSection)));
    },
    [funnelState, handleAnswer, referenceData]
  );

  const onUpdateDriver = useCallback(
    // Default to editing main driver
    (index: number = -1) => {
      handleAnswer(updateDriver(index));
    },
    [handleAnswer]
  );

  return {
    onNext,
    onNextNoAutoFormMerge,
    onPrevious,
    onEditDriver,
    onApiResponse,
    onRestartQuote,
    onRemoveDriver,
    onEditQuoteDetails,
    onEditCoverDetails,
    onEditPolicyDetails,
    onNextIfLocationIs,
    onEditReviewDriver,
    onUpdateDriver,
  };
};

export default useFlowActions;
